import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Menu, X, Wrench, User, LogOut, Bell } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { NotificationBell } from "@/components/NotificationBell";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const Navbar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, isAuthenticated, logout, loading } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [contractorId, setContractorId] = useState(null);
  const [contractorEmail, setContractorEmail] = useState(null);

  useEffect(() => {
    // Check for saved contractor info
    const savedWorkerId = localStorage.getItem("workerId");
    const savedEmail = localStorage.getItem("workerEmail");
    if (savedWorkerId) setContractorId(savedWorkerId);
    if (savedEmail) setContractorEmail(savedEmail);
  }, []);

  const navLinks = [
    { href: "/", label: t("nav.home") },
    { href: "/browse", label: t("nav.browse") },
    { href: "/jobs", label: t("nav.postJob") },
    { href: "/request", label: t("requestService.title") },
    { href: "/dashboard", label: t("dashboard.title") },
  ];

  const isActive = (path) => location.pathname === path;

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <nav className="sticky top-0 z-50 bg-white border-b border-[#dadce0]" data-testid="navbar">
      <div className="container-custom">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <div className="w-10 h-10 rounded-xl bg-[#1a73e8] flex items-center justify-center">
              <Wrench className="w-5 h-5 text-white" />
            </div>
            <span className="font-semibold text-xl text-[#202124] hidden sm:block">
              LocalPros
            </span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={`text-sm font-medium transition-colors ${
                  isActive(link.href)
                    ? "text-[#1a73e8]"
                    : "text-[#5f6368] hover:text-[#202124]"
                }`}
                data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* CTA Buttons / User Menu */}
          <div className="hidden md:flex items-center gap-3">
            {/* Notification Bell - Show for logged in users or contractors */}
            {(contractorId || (isAuthenticated && user)) && (
              <NotificationBell 
                userType={contractorId ? "contractor" : "customer"}
                userId={contractorId || user?.user_id}
                userEmail={contractorEmail || user?.email}
              />
            )}
            
            {!loading && (
              <>
                {isAuthenticated && user ? (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        className="flex items-center gap-2 rounded-full px-3 hover:bg-[#f1f3f4]"
                        data-testid="user-menu-btn"
                      >
                        {user.picture ? (
                          <img 
                            src={user.picture} 
                            alt={user.name} 
                            className="w-8 h-8 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-[#1a73e8] flex items-center justify-center text-white text-sm font-medium">
                            {user.name?.charAt(0).toUpperCase()}
                          </div>
                        )}
                        <span className="text-sm font-medium text-[#202124] max-w-[100px] truncate">
                          {user.name}
                        </span>
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56">
                      <div className="px-2 py-1.5">
                        <p className="text-sm font-medium text-[#202124]">{user.name}</p>
                        <p className="text-xs text-[#5f6368]">{user.email}</p>
                      </div>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => navigate("/notifications")}>
                        <Bell className="w-4 h-4 mr-2" />
                        Notifications
                      </DropdownMenuItem>
                      {user.role === "contractor" && user.worker_id && (
                        <DropdownMenuItem onClick={() => navigate("/dashboard")}>
                          <User className="w-4 h-4 mr-2" />
                          Dashboard
                        </DropdownMenuItem>
                      )}
                      {user.role === "contractor" && !user.worker_id && (
                        <DropdownMenuItem onClick={() => navigate("/join")}>
                          <Wrench className="w-4 h-4 mr-2" />
                          Complete Profile
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem onClick={handleLogout}>
                        <LogOut className="w-4 h-4 mr-2" />
                        Sign Out
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                ) : (
                  <>
                    <Button
                      variant="ghost"
                      asChild
                      className="text-[#5f6368] hover:text-[#202124] hover:bg-[#f1f3f4] rounded-full px-4"
                    >
                      <Link to="/login" data-testid="login-nav-btn">
                        Sign In
                      </Link>
                    </Button>
                    <Button
                      asChild
                      className="bg-[#1a73e8] hover:bg-[#1557b0] text-white rounded-full px-5"
                    >
                      <Link to="/request" data-testid="get-started-nav-btn">
                        {t("hero.getStarted")}
                      </Link>
                    </Button>
                  </>
                )}
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden flex items-center gap-2">
            <button
              className="p-2 rounded-lg hover:bg-[#f1f3f4]"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? (
                <X className="w-6 h-6 text-[#5f6368]" />
              ) : (
                <Menu className="w-6 h-6 text-[#5f6368]" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-[#dadce0]" data-testid="mobile-menu">
            <div className="flex flex-col gap-2">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                    isActive(link.href)
                      ? "bg-[#e8f0fe] text-[#1a73e8]"
                      : "text-[#5f6368] hover:bg-[#f1f3f4]"
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <hr className="my-2 border-[#dadce0]" />
              {isAuthenticated && user ? (
                <>
                  <div className="px-4 py-2 flex items-center gap-3">
                    {user.picture ? (
                      <img src={user.picture} alt={user.name} className="w-10 h-10 rounded-full" />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-[#1a73e8] flex items-center justify-center text-white font-medium">
                        {user.name?.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div>
                      <p className="text-sm font-medium text-[#202124]">{user.name}</p>
                      <p className="text-xs text-[#5f6368]">{user.email}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      handleLogout();
                      setMobileMenuOpen(false);
                    }}
                    className="px-4 py-3 rounded-lg text-sm font-medium text-red-600 hover:bg-red-50 text-left flex items-center gap-2"
                  >
                    <LogOut className="w-4 h-4" />
                    Sign Out
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to="/login"
                    onClick={() => setMobileMenuOpen(false)}
                    className="px-4 py-3 rounded-lg text-sm font-medium text-[#5f6368] hover:bg-[#f1f3f4]"
                  >
                    Sign In
                  </Link>
                  <Link
                    to="/request"
                    onClick={() => setMobileMenuOpen(false)}
                    className="mx-4 py-3 rounded-full bg-[#1a73e8] text-white text-sm font-medium text-center"
                  >
                    {t("hero.getStarted")}
                  </Link>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
